import { t } from "@airange/shared/i18n-identity-picker";
import { App } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { ADMIN_LIST_PAGINATION_STANDARD, readAdminStandardPageSize } from "./admin-table";
import { OrganizationUserPickerModal } from "./organization-user-picker-modal";
import { useSsoOrganizationUserPickerColumns } from "./sso-organization-user-picker-columns";
import {
  buildOrganizationTreeKey,
  buildOrganizationTreeNodes,
  flattenOrganizationUnits,
  normalizeExistingUserId,
  parsePickerTreeKey,
} from "./sso-organization-user-picker-model";
import type {
  SsoOrganizationDirectoryPickerBootstrap,
  SsoOrganizationPickerCustomFieldDefinition,
  SsoOrganizationPickerFilterValues,
  SsoOrganizationPickerOrganizationTree,
  SsoOrganizationPickerUser,
  SsoOrganizationUserPickerModalProps,
} from "./sso-organization-user-picker-types";

export function SsoOrganizationUserPickerModal({
  title,
  submitText,
  cancelText,
  open,
  onOpenChange,
  onSubmit,
  persistenceKey,
  selectedUsers,
  onSelectedUsersChange,
  existingUserIds = [],
  loadDirectoryPickerBootstrap,
  searchDirectoryPickerUsers,
  rowSelectionType = "checkbox",
  treeTitle = t("common.identityPicker.treeTitle"),
  tableTitlePrefix = t("common.identityPicker.tableTitlePrefix"),
  width = 1200,
  defaultColumnsState,
}: SsoOrganizationUserPickerModalProps) {
  const { message } = App.useApp();
  const [bootstrapping, setBootstrapping] = useState(false);
  const [ready, setReady] = useState(false);
  const [treeSearchValue, setTreeSearchValue] = useState("");
  const [selectedTreeKeys, setSelectedTreeKeys] = useState<string[]>([]);
  const [filterValues, setFilterValues] = useState<SsoOrganizationPickerFilterValues>({});
  const [organizationTrees, setOrganizationTrees] = useState<
    SsoOrganizationPickerOrganizationTree[]
  >([]);
  const [currentRequestUsers, setCurrentRequestUsers] = useState<SsoOrganizationPickerUser[]>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<
    SsoOrganizationPickerCustomFieldDefinition[]
  >([]);
  const bootstrapRequestRef = useRef<Promise<SsoOrganizationDirectoryPickerBootstrap> | null>(null);

  const existingUserIdSet = useMemo(() => {
    const next = new Set<number>();
    for (const item of existingUserIds) {
      const normalizedId = normalizeExistingUserId(item);
      if (normalizedId != null) {
        next.add(normalizedId);
      }
    }
    return next;
  }, [existingUserIds]);

  useEffect(() => {
    if (!open || ready) {
      return;
    }
    let cancelled = false;
    setBootstrapping(true);
    void (async () => {
      try {
        const request = bootstrapRequestRef.current ?? loadDirectoryPickerBootstrap();
        bootstrapRequestRef.current = request;
        const bootstrap = await request;
        if (cancelled) {
          return;
        }
        setOrganizationTrees(bootstrap.organization_trees);
        setCustomFieldDefinitions(bootstrap.custom_field_definitions);
        setReady(true);
        if (bootstrap.organization_trees.length > 0) {
          const firstTree = bootstrap.organization_trees[0];
          if (firstTree) {
            setSelectedTreeKeys([buildOrganizationTreeKey(firstTree.organization.id)]);
          }
        }
      } catch (error) {
        bootstrapRequestRef.current = null;
        if (cancelled) {
          return;
        }
        message.error(
          error instanceof Error ? error.message : t("common.identityPicker.loadDataFailed"),
        );
        onOpenChange(false);
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadDirectoryPickerBootstrap, message, onOpenChange, open, ready]);

  useEffect(() => {
    if (open || organizationTrees.length === 0 || selectedTreeKeys.length > 0) {
      return;
    }
    const firstTree = organizationTrees[0];
    if (firstTree) {
      setSelectedTreeKeys([buildOrganizationTreeKey(firstTree.organization.id)]);
    }
  }, [open, organizationTrees, selectedTreeKeys.length]);

  useEffect(() => {
    if (open) {
      return;
    }
    setTreeSearchValue("");
    setSelectedTreeKeys([]);
    setFilterValues({});
  }, [open]);

  const selectedPickerContext = useMemo(
    () => parsePickerTreeKey(selectedTreeKeys[0]),
    [selectedTreeKeys],
  );

  const organizationTreeMap = useMemo(
    () => new Map(organizationTrees.map((item) => [item.organization.id, item])),
    [organizationTrees],
  );

  const treeData = useMemo(
    () => buildOrganizationTreeNodes(organizationTrees, treeSearchValue),
    [organizationTrees, treeSearchValue],
  );

  const selectedTreeLabel = useMemo(() => {
    if (!selectedPickerContext) {
      return t("common.identityPicker.selectOrganization");
    }
    const organizationTree = organizationTreeMap.get(selectedPickerContext.organizationId);
    if (!organizationTree) {
      return t("common.identityPicker.selectOrganization");
    }
    if (selectedPickerContext.unitId == null) {
      return organizationTree.organization.name;
    }
    const unitMap = new Map(
      flattenOrganizationUnits(organizationTree.root_unit).map((unit) => [unit.id, unit]),
    );
    return `${organizationTree.organization.name} / ${unitMap.get(selectedPickerContext.unitId)?.name ?? t("common.identityPicker.organizationNode")}`;
  }, [organizationTreeMap, selectedPickerContext]);

  const selectedUserIds = useMemo(() => selectedUsers.map((item) => item.id), [selectedUsers]);

  const { mergedDefaultColumnsState, userColumns } = useSsoOrganizationUserPickerColumns({
    customFieldDefinitions,
    ...(defaultColumnsState ? { defaultColumnsState } : {}),
    filterValues,
    setFilterValues,
  });

  const tableRequestParams = useMemo(
    () => ({
      selectedTreeKey: selectedTreeKeys[0] ?? "",
      excludedUserIds: Array.from(existingUserIdSet).join(","),
      username: filterValues.username ?? "",
      display_name: filterValues.display_name ?? "",
    }),
    [existingUserIdSet, filterValues.display_name, filterValues.username, selectedTreeKeys],
  );

  return (
    <OrganizationUserPickerModal<SsoOrganizationPickerUser>
      title={title}
      submitText={submitText}
      cancelText={cancelText}
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      columns={userColumns}
      rowKey="id"
      selectedRowKeys={selectedUserIds}
      onSelectionChange={(_keys, rows) => {
        const visibleIds = new Set(currentRequestUsers.map((item) => item.id));
        const nextMap = new Map(selectedUsers.map((item) => [item.id, item]));
        for (const userId of visibleIds) {
          nextMap.delete(userId);
        }
        for (const row of rows) {
          nextMap.set(row.id, row);
        }
        onSelectedUsersChange(
          Array.from(nextMap.values()).sort((left, right) =>
            left.username.localeCompare(right.username),
          ),
        );
      }}
      rowSelectionType={rowSelectionType}
      persistenceKey={persistenceKey}
      defaultColumnsState={mergedDefaultColumnsState}
      emptyText={
        selectedPickerContext
          ? t("common.identityPicker.emptyInCurrentScope")
          : t("common.identityPicker.emptyBeforeOrganization")
      }
      tableTitle={`${tableTitlePrefix} · ${selectedTreeLabel}`}
      treeTitle={treeTitle}
      treeData={treeData}
      treeLoading={bootstrapping}
      treeSearchValue={treeSearchValue}
      onTreeSearchValueChange={setTreeSearchValue}
      selectedTreeKeys={selectedTreeKeys}
      onTreeSelect={(keys) => {
        const selectedKey = keys[0];
        setSelectedTreeKeys(selectedKey === undefined ? [] : [selectedKey]);
        setFilterValues({});
      }}
      width={width}
      tableProps={{
        params: tableRequestParams,
        request: async (params) => {
          if (!selectedPickerContext) {
            setCurrentRequestUsers([]);
            return { data: [], total: 0, success: true };
          }
          const requestParams = params as typeof tableRequestParams & {
            current?: number;
            pageSize?: number;
          };
          const response = await searchDirectoryPickerUsers({
            organization_id: selectedPickerContext.organizationId,
            ...(selectedPickerContext.unitId == null ? {} : { organization_unit_id: selectedPickerContext.unitId }),
            include_descendants: true,
            ...(requestParams.username ? { username: requestParams.username } : {}),
            ...(requestParams.display_name ? { full_name: requestParams.display_name } : {}),
            exclude_user_ids: Array.from(existingUserIdSet),
            page: Number(params.current) || 1,
            page_size: readAdminStandardPageSize(Number(params.pageSize)),
          });
          setCurrentRequestUsers(response.items);
          return {
            data: response.items,
            total: response.total,
            success: true,
          };
        },
        loading: bootstrapping,
        pagination: ADMIN_LIST_PAGINATION_STANDARD,
      }}
    />
  );
}
